/**
 * @file ui/fields/DateField.tsx
 * @description 使用 Ant Design Form.Item 封装的日期选择字段（垂直布局）。
 */

import type { ErrorKey } from '@core/validation/errorKeys'
import { useT } from '@ui/i18n/useT'
import { useMemoizedFn } from 'ahooks'
import { ConfigProvider, DatePicker, Form } from 'antd'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'

import {
  DATE_FIELD_DISPLAY_FORMAT,
  DATE_FIELD_STORAGE_FORMAT,
  resolveDateFieldValue,
} from './dateFieldValue'
import { resolveErrorMessage } from './error'
import { buildFormItemLayout, type FieldFormLayout, type FieldFormLayoutOptions } from './formItemLayout'

interface DateFieldProps {
  value?: string // ISO format: YYYY-MM-DD
  onChange?: (value: string) => void
  label?: string
  placeholder?: string
  hint?: string
  formatHint?: string
  required?: boolean
  disabled?: boolean
  error?: ErrorKey
  minDate?: string
  minBoundary?: 'inclusive' | 'exclusive'
  maxDate?: string
  fieldPath?: string
  className?: string
  style?: React.CSSProperties
  formLayout?: FieldFormLayout
  labelWidth?: FieldFormLayoutOptions['labelWidth']
}

/**
 * DateField：使用 Ant Design Form.Item 的日期选择组件（垂直布局）。
 */
export function DateField({
  value,
  onChange,
  label,
  placeholder,
  hint,
  formatHint,
  required,
  disabled,
  error,
  minDate,
  minBoundary = 'inclusive',
  maxDate,
  fieldPath,
  className,
  style,
  formLayout,
  labelWidth,
}: DateFieldProps) {
  const { t } = useT()
  const { componentDisabled } = ConfigProvider.useConfig()
  const isFieldDisabled = disabled || componentDisabled

  const errorText = resolveErrorMessage(t, error)
  const validateStatus = errorText ? 'error' : undefined
  const dayjsValue = resolveDateFieldValue(value)
  const displayHint = hint ?? formatHint
  const displayPlaceholder = placeholder ?? formatHint ?? t('placeholders.date')
  const formItemLayout = buildFormItemLayout({ formLayout, labelWidth })

  const handleChange = useMemoizedFn((date: Dayjs | null) => {
    onChange?.(date ? date.format(DATE_FIELD_STORAGE_FORMAT) : '')
  })

  const disabledDate = useMemoizedFn((current: Dayjs) => {
    if (!current) return false
    if (minDate) {
      const min = dayjs(minDate)
      if (minBoundary === 'exclusive' && !current.isAfter(min, 'day')) return true
      if (minBoundary === 'inclusive' && current.isBefore(min, 'day')) return true
    }
    if (maxDate && current.isAfter(dayjs(maxDate), 'day')) return true
    return false
  })

  // 必填字段的黄色背景样式
  const pickerClassName = required && !isFieldDisabled && !value ? 'field-required-empty' : undefined

  return (
    <Form.Item
      {...formItemLayout}
      label={label}
      required={required}
      validateStatus={validateStatus}
      help={errorText}
      extra={displayHint}
      data-field-path={fieldPath}
      className={className}
      style={style}
    >
      <DatePicker
        value={dayjsValue}
        onChange={handleChange}
        placeholder={displayPlaceholder}
        disabled={isFieldDisabled}
        disabledDate={disabledDate}
        format={DATE_FIELD_DISPLAY_FORMAT}
        className={pickerClassName}
        style={{ width: '100%' }}
      />
    </Form.Item>
  )
}
